import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { PasswordInput } from './ui/password-input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { authService } from '../services/auth';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid or missing reset token');
        setValidating(false);
        return;
      }

      try {
        const response = await authService.validateResetToken(token);
        setTokenValid(response.valid);
        if (!response.valid) {
          setError('Invalid or expired reset token');
        }
      } catch (err) {
        setError('Error validating reset token');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePasswords = () => {
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(
        formData.newPassword
      )
    ) {
      setError(
        'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
      );
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !tokenValid) return;

    setError('');

    if (!validatePasswords()) {
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword({
        token,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.message || 'An error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4'></div>
          <p className='text-gray-600'>Validating reset token...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-6 sm:space-y-8'>
          <div className='text-center'>
            <div className='flex justify-center mb-4'>
              <div className='w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center'>
                <span className='text-red-600 text-2xl'>❌</span>
              </div>
            </div>
            <h2 className='heading-2 text-gray-900 mb-2'>Invalid Reset Link</h2>
            <p className='body-small text-gray-600 mb-6'>
              {error || 'This password reset link is invalid or has expired'}
            </p>
            <Link
              to='/forgot-password'
              className='btn-primary inline-flex items-center px-6 py-3 text-base font-medium'
            >
              Request New Reset Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-6 sm:space-y-8'>
          <div className='text-center'>
            <div className='flex justify-center mb-4'>
              <div className='w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center'>
                <span className='text-green-600 text-2xl'>✅</span>
              </div>
            </div>
            <h2 className='heading-2 text-gray-900 mb-2'>
              Password Reset Successful
            </h2>
            <p className='body-small text-gray-600 mb-6'>
              Your password has been successfully reset. You will be redirected
              to the login page in a moment.
            </p>
            <Link
              to='/login'
              className='btn-primary inline-flex items-center px-6 py-3 text-base font-medium'
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full space-y-6 sm:space-y-8'>
        {/* Brand Section */}
        <div className='text-center'>
          <div className='flex justify-center mb-4'>
            <div className='w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-2xl flex items-center justify-center shadow-lg'>
              <span className='text-white font-bold text-2xl'>P</span>
            </div>
          </div>
          <h2 className='heading-2 text-gray-900 mb-2'>Reset Your Password</h2>
          <p className='body-small text-gray-600'>
            Enter your new password below
          </p>
        </div>

        <Card className='card-enhanced shadow-xl border-0'>
          <div className='p-6 sm:p-8'>
            {/* Error Message */}
            {error && (
              <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg'>
                <p className='text-sm text-red-800'>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className='space-y-6'>
              <div className='form-field'>
                <Label htmlFor='newPassword' className='form-label'>
                  New Password
                </Label>
                <PasswordInput
                  id='newPassword'
                  name='newPassword'
                  autoComplete='new-password'
                  required
                  placeholder='Enter your new password'
                  value={formData.newPassword}
                  onChange={handleChange}
                  disabled={loading}
                  className='form-input focus-ring transition-smooth'
                />
                <p className='text-xs text-gray-500 mt-1'>
                  Must be at least 8 characters with uppercase, lowercase,
                  number, and special character
                </p>
              </div>

              <div className='form-field'>
                <Label htmlFor='confirmPassword' className='form-label'>
                  Confirm New Password
                </Label>
                <PasswordInput
                  id='confirmPassword'
                  name='confirmPassword'
                  autoComplete='new-password'
                  required
                  placeholder='Confirm your new password'
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  className='form-input focus-ring transition-smooth'
                />
              </div>

              <Button
                type='submit'
                disabled={
                  loading || !formData.newPassword || !formData.confirmPassword
                }
                className='btn-primary w-full h-12 text-base font-medium'
              >
                {loading ? (
                  <div className='flex items-center justify-center'>
                    <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2'></div>
                    Resetting Password...
                  </div>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>

            {/* Back to Login */}
            <div className='mt-6 text-center'>
              <p className='body-small text-gray-600'>
                Remember your password?{' '}
                <Link
                  to='/login'
                  className='font-medium text-primary hover:text-primary/80 transition-colors'
                >
                  Back to Login
                </Link>
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
